import React from "react";
import { useTranslation } from "react-i18next";
import { Box, ListItemIcon, MenuItem, Select, useTheme } from "@mui/material";
import { NearMe, PriceCheck, StarRounded } from "@mui/icons-material";
import Typography from "components/basic/typography";
import { EvChargerServiceSortBy } from "generated/graphql";

export const SORT_OPTIONS = [
  {
    id: EvChargerServiceSortBy.Distance,
    label: "select.closest",
    icon: NearMe,
  },
  {
    id: EvChargerServiceSortBy.Price,
    label: "select.cheapest",
    icon: PriceCheck,
  },
  {
    id: EvChargerServiceSortBy.Rating,
    label: "select.top_rated",
    icon: StarRounded,
  },
] as const;

interface SortFilterProps {
  value: EvChargerServiceSortBy;
  onChange: (value: EvChargerServiceSortBy) => void;
}

/**
 * Lets the user order the installers that serve their address by distance,
 * price or Google rating. Ordering only — which installers appear is decided by
 * each vendor's service radius on the server.
 */
const SortFilter: React.FC<SortFilterProps> = ({ value, onChange }) => {
  const theme = useTheme();
  const { t } = useTranslation("estimator");

  const renderOption = (optionId: EvChargerServiceSortBy) => {
    const option =
      SORT_OPTIONS.find((candidate) => candidate.id === optionId) ??
      SORT_OPTIONS[0];
    const Icon = option.icon;

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Icon sx={{ fontSize: 18 }} />
        {t(option.label)}
      </Box>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
      }}
    >
      <Typography variant="body1" style={{ fontWeight: "bold" }}>
        {t("select.sort_by")}
      </Typography>

      <Select
        size="small"
        value={value}
        onChange={(event) =>
          onChange(event.target.value as EvChargerServiceSortBy)
        }
        renderValue={(selected) =>
          renderOption(selected as EvChargerServiceSortBy)
        }
        sx={{
          minWidth: 160,
          backgroundColor: theme.palette.common.white,
        }}
      >
        {SORT_OPTIONS.map((option) => {
          const Icon = option.icon;
          return (
            <MenuItem key={option.id} value={option.id}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Icon sx={{ fontSize: 18 }} />
              </ListItemIcon>
              {t(option.label)}
            </MenuItem>
          );
        })}
      </Select>
    </div>
  );
};

export default SortFilter;
